import React, { useEffect, useState, useCallback } from 'react';
import { Card, Form, Input, Select, Button, message, Tabs } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { systemConfigApi } from '../../services/api';

const { Option } = Select;
const { TabPane } = Tabs;

const modelConfigs = {
  '讯飞星火': {
    fields: ['api_key', 'api_secret', 'app_id'],
    defaults: { api_base: 'wss://spark-api.xf-yun.com/v3.5/chat' },
  },
  'OpenAI': {
    fields: ['api_key', 'api_base'],
    defaults: { api_base: 'https://api.openai.com/v1' },
  },
  '阿里云百炼': {
    fields: ['api_key'],
    defaults: { api_base: 'https://dashscope.aliyuncs.com/api/v1' },
  },
  'DeepSeek': {
    fields: ['api_key', 'api_base'],
    defaults: { api_base: 'https://api.deepseek.com' },
  },
};

const imageModelConfigs = {
  '阿里云百炼': {
    fields: ['api_key'],
    defaults: { api_base: 'https://dashscope.aliyuncs.com/api/v1' },
  },
  'OpenAI': {
    fields: ['api_key', 'api_base'],
    defaults: { api_base: 'https://api.openai.com/v1' },
  },
  'APIB AI': {
    fields: ['api_key'],
    defaults: { api_base: 'https://api.apib.ai/v1' },
  },
};

const wanVideoConfigs = {
  '阿里云百炼': {
    fields: ['api_key'],
    defaults: { api_base: 'https://dashscope.aliyuncs.com/api/v1' },
    models: [
      { value: 'wanx2.7', label: '万相2.7 (有声，支持参考视频，阿里最新模型)' },
      { value: 'wanx2.6', label: '万相2.6 (支持有声，支持参考视频)' },
      { value: 'wanx2.1', label: '万相2.1' },
    ],
  },
};

const gorkVideoConfigs = {
  'Grok': {
    fields: ['api_key', 'upload_key'],
    defaults: { api_base: 'https://ark.cn-beijing.volces.com/api/v3' },
    models: [
      { value: 'grok-imagine-1.5-video-apimart', label: 'Grok Imagine 1.5 Video (图生视频)' },
    ],
  },
};

const seedanceVideoConfigs = {
  '火山引擎': {
    fields: ['api_key'],
    defaults: { api_base: 'https://ark.cn-beijing.volces.com/api/v3' },
    models: [
      { value: 'doubao-seedance-2-0-260128', label: 'Seedance-2.0 (超高并发,支持真人)' },
      { value: 'doubao-seedance-2-0-fast-260128', label: 'Seedance-2.0 Fast (超高并发,支持真人)' },
    ],
  },
};

const minimaxVideoConfigs = {
  'MiniMax': {
    fields: ['api_key'],
    defaults: { api_base: 'https://api.minimaxi.com' },
    models: [
      { value: 'MiniMax-H3', label: 'MiniMax-H3 (参考生视频,支持参考视频+参考音频)' },
      { value: 'MiniMax-H3-2.0', label: 'MiniMax-H3 2.0 (最新版本)' },
    ],
  },
};

const AISettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const llmProvider = Form.useWatch('llm_provider', form);
  const imageProvider = Form.useWatch('image_provider', form);
  const wanVideoProvider = Form.useWatch('wan_video_provider', form);
  const gorkVideoProvider = Form.useWatch('gork_video_provider', form);
  const seedanceVideoProvider = Form.useWatch('seedance_video_provider', form);
  const minimaxVideoProvider = Form.useWatch('minimax_video_provider', form);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await systemConfigApi.getAISettings();
      const settings = response.data.settings;
      
      form.setFieldsValue({
        llm_provider: settings.llm_provider || '阿里云百炼',
        llm_api_key: settings.llm_api_key || '',
        llm_api_secret: settings.llm_api_secret || '',
        llm_app_id: settings.llm_app_id || '',
        llm_api_base: settings.llm_api_base || '',
        llm_model: settings.llm_model || '',
        image_provider: settings.image_provider || '阿里云百炼',
        image_api_key: settings.image_api_key || '',
        image_api_base: settings.image_api_base || '',
        image_model: settings.image_model || '',
        wan_video_provider: settings.wan_video_provider || '阿里云百炼',
        wan_video_api_key: settings.wan_video_api_key || '',
        wan_video_api_base: settings.wan_video_api_base || '',
        wan_video_model: settings.wan_video_model || '',
        gork_video_provider: settings.gork_video_provider || 'Grok',
        gork_video_api_key: settings.gork_video_api_key || '',
        gork_video_api_base: settings.gork_video_api_base || '',
        gork_video_model: settings.gork_video_model || '',
        gork_video_upload_key: settings.gork_video_upload_key || '',
        seedance_video_provider: settings.seedance_video_provider || '火山引擎',
        seedance_video_api_key: settings.seedance_video_api_key || '',
        seedance_video_api_base: settings.seedance_video_api_base || '',
        seedance_video_model: settings.seedance_video_model || '',
        minimax_video_provider: settings.minimax_video_provider || 'MiniMax',
        minimax_video_api_key: settings.minimax_video_api_key || '',
        minimax_video_api_base: settings.minimax_video_api_base || '',
        minimax_video_model: settings.minimax_video_model || '',
      });
    } catch (error) {
      console.error('加载AI设置失败:', error);
      message.error('加载AI设置失败');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleLLMProviderChange = (value) => {
    const config = modelConfigs[value];
    if (config && config.defaults) {
      form.setFieldsValue({
        llm_api_base: config.defaults.api_base || '',
      });
    }
  };

  const handleImageProviderChange = (value) => {
    const config = imageModelConfigs[value];
    if (config && config.defaults) {
      form.setFieldsValue({
        image_api_base: config.defaults.api_base || '',
      });
    }
  };

  const handleWanVideoProviderChange = (value) => {
    const config = wanVideoConfigs[value];
    const fields = { wan_video_model: '' };
    if (config && config.defaults) {
      fields.wan_video_api_base = config.defaults.api_base || '';
    }
    form.setFieldsValue(fields);
  };

  const handleGorkVideoProviderChange = (value) => {
    const config = gorkVideoConfigs[value];
    const fields = { gork_video_model: '' };
    if (config && config.defaults) {
      fields.gork_video_api_base = config.defaults.api_base || '';
    }
    form.setFieldsValue(fields);
  };

  const handleSeedanceVideoProviderChange = (value) => {
    const config = seedanceVideoConfigs[value];
    const fields = { seedance_video_model: '' };
    if (config && config.defaults) {
      fields.seedance_video_api_base = config.defaults.api_base || '';
    }
    form.setFieldsValue(fields);
  };

  const handleMiniMaxVideoProviderChange = (value) => {
    const config = minimaxVideoConfigs[value];
    const fields = { minimax_video_model: '' };
    if (config && config.defaults) {
      fields.minimax_video_api_base = config.defaults.api_base || '';
    }
    form.setFieldsValue(fields);
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const configs = {
        llm_provider: values.llm_provider,
        llm_api_key: values.llm_api_key,
        llm_api_secret: values.llm_api_secret,
        llm_app_id: values.llm_app_id,
        llm_api_base: values.llm_api_base,
        llm_model: values.llm_model,
        image_provider: values.image_provider,
        image_api_key: values.image_api_key,
        image_api_base: values.image_api_base,
        image_model: values.image_model,
        wan_video_provider: values.wan_video_provider,
        wan_video_api_key: values.wan_video_api_key,
        wan_video_api_base: values.wan_video_api_base,
        wan_video_model: values.wan_video_model,
        gork_video_provider: values.gork_video_provider,
        gork_video_api_key: values.gork_video_api_key,
        gork_video_api_base: values.gork_video_api_base,
        gork_video_model: values.gork_video_model,
        gork_video_upload_key: values.gork_video_upload_key,
        seedance_video_provider: values.seedance_video_provider,
        seedance_video_api_key: values.seedance_video_api_key,
        seedance_video_api_base: values.seedance_video_api_base,
        seedance_video_model: values.seedance_video_model,
        minimax_video_provider: values.minimax_video_provider,
        minimax_video_api_key: values.minimax_video_api_key,
        minimax_video_api_base: values.minimax_video_api_base,
        minimax_video_model: values.minimax_video_model,
      };

      await systemConfigApi.updateConfigs(configs);
      message.success('AI设置已保存');
    } catch (error) {
      console.error('保存AI设置失败:', error);
      message.error('保存AI设置失败');
    } finally {
      setSaving(false);
    }
  };

  const getLLMFields = () => {
    const config = modelConfigs[llmProvider];
    return config ? config.fields : ['api_key'];
  };

  const getImageFields = () => {
    const config = imageModelConfigs[imageProvider];
    return config ? config.fields : ['api_key'];
  };

  const getWanVideoFields = () => {
    const config = wanVideoConfigs[wanVideoProvider];
    return config ? config.fields : ['api_key'];
  };

  const getGorkVideoFields = () => {
    const config = gorkVideoConfigs[gorkVideoProvider];
    return config ? config.fields : ['api_key'];
  };

  const getSeedanceVideoFields = () => {
    const config = seedanceVideoConfigs[seedanceVideoProvider];
    return config ? config.fields : ['api_key'];
  };

  const getMiniMaxVideoFields = () => {
    const config = minimaxVideoConfigs[minimaxVideoProvider];
    return config ? config.fields : ['api_key'];
  };

  const llmFields = getLLMFields();
  const imageFields = getImageFields();
  const wanVideoFields = getWanVideoFields();
  const gorkVideoFields = getGorkVideoFields();
  const seedanceVideoFields = getSeedanceVideoFields();
  const minimaxVideoFields = getMiniMaxVideoFields();

  return (
    <div>
      <h2>AI模型设置</h2>
      <Card loading={loading}>
        <Form form={form} onFinish={handleSave} layout="vertical">
          <Tabs defaultActiveKey="llm">
            <TabPane tab="大模型配置" key="llm">
              <Form.Item name="llm_provider" label="模型提供商">
                <Select onChange={handleLLMProviderChange}>
                  {Object.keys(modelConfigs).map(provider => (
                    <Option key={provider} value={provider}>{provider}</Option>
                  ))}
                </Select>
              </Form.Item>
              
              {llmFields.includes('api_key') && (
                <Form.Item name="llm_api_key" label="API Key">
                  <Input.Password placeholder="请输入 API Key" />
                </Form.Item>
              )}
              
              {llmFields.includes('api_secret') && (
                <Form.Item name="llm_api_secret" label="API Secret">
                  <Input.Password placeholder="请输入 API Secret" />
                </Form.Item>
              )}
              
              {llmFields.includes('app_id') && (
                <Form.Item name="llm_app_id" label="App ID">
                  <Input placeholder="请输入 App ID" />
                </Form.Item>
              )}
              
              {llmFields.includes('api_base') && (
                <Form.Item name="llm_api_base" label="API Base URL">
                  <Input placeholder="请输入 API Base URL" />
                </Form.Item>
              )}
              
              <Form.Item name="llm_model" label="模型名称（可选）">
                <Select
                  showSearch
                  allowClear
                  placeholder="请选择或输入模型名称"
                  mode="tags"
                  maxTagCount={1}
                >
                  <Option value="">不指定</Option>
                  <Option value="qwen-flash-character">qwen-flash-character</Option>
                  <Option value="qwen3.5-plus">qwen3.5-plus</Option>
                  <Option value="qwen3.5-plus-2026-02-15">qwen3.5-plus-2026-02-15</Option>
                  <Option value="deepseek-v3.2">deepseek-v3.2</Option>
                  <Option value="kimi-k2.5">kimi-k2.5</Option>
                  <Option value="qwen3-max-2026-01-23">qwen3-max-2026-01-23</Option>
                </Select>
              </Form.Item>
            </TabPane>

            <TabPane tab="出图模型配置" key="image">
              <Form.Item name="image_provider" label="模型提供商">
                <Select onChange={handleImageProviderChange}>
                  {Object.keys(imageModelConfigs).map(provider => (
                    <Option key={provider} value={provider}>{provider}</Option>
                  ))}
                </Select>
              </Form.Item>
              
              {imageFields.includes('api_key') && (
                <Form.Item name="image_api_key" label="API Key">
                  <Input.Password placeholder="请输入 API Key" />
                </Form.Item>
              )}
              
              {imageFields.includes('api_base') && (
                <Form.Item name="image_api_base" label="API Base URL">
                  <Input placeholder="请输入 API Base URL" />
                </Form.Item>
              )}
              
              <Form.Item name="image_model" label="模型名称（可选）">
                <Select
                  showSearch
                  allowClear
                  placeholder="请选择或输入模型名称"
                  mode="tags"
                  maxTagCount={1}
                >
                  <Option value="">不指定</Option>
                  <Option value="qwen-image-max">qwen-image-max</Option>
                  <Option value="qwen-image-plus">qwen-image-plus</Option>
                  <Option value="qwen-image-2.0-pro">qwen-image-2.0-pro</Option>
                  <Option value="qwen-image-2.0">qwen-image-2.0</Option>
                  <Option value="gpt-image-2-official">GPT-Image-2 Official</Option>
                  <Option value="dall-e-3">dall-e-3</Option>
                  <Option value="dall-e-2">dall-e-2</Option>
                </Select>
              </Form.Item>
            </TabPane>

            <TabPane tab="文生视频配置(万相)" key="wan-video">
              <Form.Item name="wan_video_provider" label="模型提供商">
                <Select onChange={handleWanVideoProviderChange}>
                  {Object.keys(wanVideoConfigs).map(provider => (
                    <Option key={provider} value={provider}>{provider}</Option>
                  ))}
                </Select>
              </Form.Item>
              
              {wanVideoFields.includes('api_key') && (
                <Form.Item name="wan_video_api_key" label="API Key">
                  <Input.Password placeholder="请输入 API Key" />
                </Form.Item>
              )}
              
              {wanVideoFields.includes('api_base') && (
                <Form.Item name="wan_video_api_base" label="API Base URL">
                  <Input placeholder="请输入 API Base URL" />
                </Form.Item>
              )}
              
              <Form.Item name="wan_video_model" label="模型名称（可选）">
                <Select
                  showSearch
                  allowClear
                  placeholder="请选择或输入模型名称"
                  mode="tags"
                  maxTagCount={1}
                >
                  <Option value="">不指定</Option>
                  {wanVideoProvider && wanVideoConfigs[wanVideoProvider]?.models?.map(model => (
                    <Option key={model.value} value={model.value}>{model.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </TabPane>

            <TabPane tab="图生视频配置(Grok)" key="gork-video">
              <Form.Item name="gork_video_provider" label="模型提供商">
                <Select onChange={handleGorkVideoProviderChange}>
                  {Object.keys(gorkVideoConfigs).map(provider => (
                    <Option key={provider} value={provider}>{provider}</Option>
                  ))}
                </Select>
              </Form.Item>
              
              {gorkVideoFields.includes('api_key') && (
                <Form.Item name="gork_video_api_key" label="API Key">
                  <Input.Password placeholder="请输入 API Key" />
                </Form.Item>
              )}
              
              {gorkVideoFields.includes('upload_key') && (
                <Form.Item name="gork_video_upload_key" label="Upload Key（图片上传服务器密钥）">
                  <Input.Password placeholder="请输入 Upload Key，默认值: your_REF_KEY" />
                </Form.Item>
              )}
              
              {gorkVideoFields.includes('api_base') && (
                <Form.Item name="gork_video_api_base" label="API Base URL">
                  <Input placeholder="请输入 API Base URL" />
                </Form.Item>
              )}
              
              <Form.Item name="gork_video_model" label="模型名称（可选）">
                <Select
                  showSearch
                  allowClear
                  placeholder="请选择或输入模型名称"
                  mode="tags"
                  maxTagCount={1}
                >
                  <Option value="">不指定</Option>
                  {gorkVideoProvider && gorkVideoConfigs[gorkVideoProvider]?.models?.map(model => (
                    <Option key={model.value} value={model.value}>{model.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </TabPane>

            <TabPane tab="参考生视频配置(Seedance)" key="seedance-video">
              <Form.Item name="seedance_video_provider" label="模型提供商">
                <Select onChange={handleSeedanceVideoProviderChange}>
                  {Object.keys(seedanceVideoConfigs).map(provider => (
                    <Option key={provider} value={provider}>{provider}</Option>
                  ))}
                </Select>
              </Form.Item>
              
              {seedanceVideoFields.includes('api_key') && (
                <Form.Item name="seedance_video_api_key" label="API Key">
                  <Input.Password placeholder="请输入 API Key" />
                </Form.Item>
              )}
              
              {seedanceVideoFields.includes('api_base') && (
                <Form.Item name="seedance_video_api_base" label="API Base URL">
                  <Input placeholder="请输入 API Base URL" />
                </Form.Item>
              )}
              
              <Form.Item name="seedance_video_model" label="模型名称（可选）">
                <Select
                  showSearch
                  allowClear
                  placeholder="请选择或输入模型名称"
                  mode="tags"
                  maxTagCount={1}
                >
                  <Option value="">不指定</Option>
                  {seedanceVideoProvider && seedanceVideoConfigs[seedanceVideoProvider]?.models?.map(model => (
                    <Option key={model.value} value={model.value}>{model.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </TabPane>

            <TabPane tab="参考生视频配置(MiniMax H3)" key="minimax-video">
              <Form.Item name="minimax_video_provider" label="模型提供商">
                <Select onChange={handleMiniMaxVideoProviderChange}>
                  {Object.keys(minimaxVideoConfigs).map(provider => (
                    <Option key={provider} value={provider}>{provider}</Option>
                  ))}
                </Select>
              </Form.Item>
              
              {minimaxVideoFields.includes('api_key') && (
                <Form.Item name="minimax_video_api_key" label="API Key">
                  <Input.Password placeholder="请输入 MiniMax API Key" />
                </Form.Item>
              )}
              
              {minimaxVideoFields.includes('api_base') && (
                <Form.Item name="minimax_video_api_base" label="API Base URL">
                  <Input placeholder="请输入 API Base URL" />
                </Form.Item>
              )}
              
              <Form.Item name="minimax_video_model" label="模型名称（可选）">
                <Select
                  showSearch
                  allowClear
                  placeholder="请选择或输入模型名称"
                  mode="tags"
                  maxTagCount={1}
                >
                  <Option value="">不指定</Option>
                  {minimaxVideoProvider && minimaxVideoConfigs[minimaxVideoProvider]?.models?.map(model => (
                    <Option key={model.value} value={model.value}>{model.label}</Option>
                  ))}
                </Select>
              </Form.Item>
              
              <div style={{ padding: '8px 0', color: '#888', fontSize: '12px' }}>
                <p style={{ margin: '0 0 4px 0' }}>📌 MiniMax H3 支持的功能：</p>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>文本生视频：仅使用文字描述生成视频</li>
                  <li>参考图生视频：上传图片作为视觉参考</li>
                  <li>参考视频生视频：上传视频作为风格/动作参考</li>
                  <li>参考音频生视频：上传音频用于音色参考</li>
                  <li>多模态组合：文本 + 视频 + 音频组合使用</li>
                </ul>
                <p style={{ margin: '4px 0 0 0' }}>⏱️ 视频时长：5-15秒 | 🎬 分辨率：480P/720P/1080P/2K</p>
              </div>
            </TabPane>
          </Tabs>

          <Form.Item style={{ marginTop: 24 }}>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving} size="large">
              保存设置
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default AISettings;